import type { SagaIterator } from 'redux-saga';
import { put, delay } from 'redux-saga/effects';
import { call as typedCall, select as typedSelect } from 'typed-redux-saga';

import api from '../Services/Api';
import { finishLoading, startLoading } from '../Redux/LoadingActions';
import { apiFetch, apiPost, apiPut } from './ApiSaga';
import { setSagaMessage } from '../Redux/ApplicationActions';
import { addPermission, storePermissions } from '../Redux/PermissionActions';
import { applyDefaultFeaturePermissions, ENTITY_TYPE_CHATROOM } from '../Utils/hasPermissions';
import type { AddPermission, AddPermissionToAuthorizedEntities, UpdatePermission } from '../Redux/PermissionActions';
import { parseResponseTextError } from '../Utils/flattenError';
import type { ApiAddPermissionResponse, ApiFeaturePermissionsResponse } from '../Utils/apiTypes';
import { InvalidResponseCode, ResourceErrorInterface } from 'tg-resources';
import type { RootState } from '../Redux';
import { getDonor } from '../Redux/DonorActions';

export function* getFeaturePermissionsSaga(): SagaIterator<void> {
  // Debounce loading permissions to avoid multiple calls in quick succession
  // as can happen when a user is added to multiple follower groups when
  // a new case is created.
  yield delay(500);

  yield put(startLoading('permissions'));

  const { result, error } = yield* typedCall(apiFetch<ApiFeaturePermissionsResponse>, api.txp.featurePermissions);

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isNetworkError) {
        yield put(setSagaMessage('Loading permissions failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Loading permissions failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Loading permissions failed', 'Try again later', ''));
    }
    yield put(finishLoading('permissions'));
  } else {
    // AP has no way to add BasicUser permissions yet so apply default permissions here
    const { accessLevel } = yield* typedSelect((state: RootState) => state.auth || {});
    const featurePermissions = applyDefaultFeaturePermissions(accessLevel, result);

    yield put(storePermissions(featurePermissions));

    yield put(finishLoading('permissions'));
  }
}

export function* addPermissionSaga(action: AddPermission): SagaIterator<void> {
  const {
    entityType,
    entityId,
    subEntityType,
    subEntityId,
    cascade,
    read,
    update,
    canDelete,
    authorizedType,
    authorizedId,
    displayToast,
  } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const { result, error } = yield* typedCall(apiPost<ApiAddPermissionResponse>, api.txp.addPermission, undefined, {
    target_type: entityType,
    target_id: entityId,
    subtarget_type: subEntityType,
    subtarget_id: subEntityId,
    cascade,
    read,
    update,
    delete: canDelete,
    authorized_type: authorizedType,
    authorized_id: authorizedId,
  });

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Adding permission failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Adding permission failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Adding permission failed', 'Try again later', ''));
    }
  } else {
    // Refetch the donot to make sure we get the latest case admin list
    if (entityType === 'Donor' && update) {
      yield put(getDonor(entityId));
    }

    // Delay so the update dialog can close, otherwise the toast goes away too quickly
    yield delay(500);

    if (displayToast) {
      if (entityType === ENTITY_TYPE_CHATROOM) {
        yield put(setSagaMessage('', 'Manager added', ''));
      } else {
        yield put(setSagaMessage('', 'Permission added', ''));
      }
    }
  }
}

export function* addPermissionToAuthorizedEntitiesSaga(action: AddPermissionToAuthorizedEntities): SagaIterator<void> {
  const {
    authorizedEntities,
    entityType,
    entityId,
    subEntityType,
    subEntityId,
    cascade,
    read,
    update,
    canDelete,
    displayToast,
  } = action;

  if (!authorizedEntities) {
    return;
  }

  for (let i = 0; i < authorizedEntities.length; i += 1) {
    const { authorizedType, authorizedId, displayName } = authorizedEntities[i];
    yield put(
      addPermission(
        entityType,
        entityId,
        subEntityType,
        subEntityId,
        cascade,
        read,
        update,
        canDelete,
        authorizedType,
        authorizedId,
        displayName,
        displayToast,
      ),
    );
  }
}

// This saga is currently only triggered in CasePermissions.tsx for
// updating the organization read access to a case. Instead of the client
// knowing what permissions to add or remove to update the organization's
// access to a case, it should be better encapsulated on the server and
// we should just call an endpoint to update organization case access
export function* updatePermissionSaga(action: UpdatePermission): SagaIterator<void> {
  const { entityType, entityId, permissionId, read, update, canDelete } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const { error } = yield* typedCall(
    apiPut,
    api.txp.updatePermission,
    { permissionId },
    { read, update, delete: canDelete },
  );

  if (error) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        const errorMessage = parseResponseTextError(error.responseText);
        yield put(setSagaMessage('', errorMessage, ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Updating permission failed', 'are you online?', ''));
      } else {
        yield put(setSagaMessage('Updating permission failed', 'try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Updating permission failed', 'try again later', ''));
    }
  } else {
    put(setSagaMessage('', 'Permission Updated', ''));
  }
}
